<template>
    <div>
        <slot name="badge"></slot>

        <span v-if="hasProRight">
            <span
                v-if="showBadge"
                class="tinyBadge tinyBadge--green float-right margin-top-5">
                {{ trans('SEARCH_PRO_BADGE_TEXT') }}
            </span>
        </span>

        <span v-else-if="showBadge">
            <template v-if="userFeature">
                <Tooltip
                    v-if="entityId === 'gcs' && !hasCloudRight"
                    :settings="{ target: '.badgeTooltipGcsUpgrade' }">
                    <i
                        class="fa fa-times-circle text-color--red float-right"
                        aria-hidden="true"></i>
                </Tooltip>
                <Tooltip
                    v-else
                    :content="trans('SEARCH_PREMIUM_CONTENT_ACCESS')">
                    <i
                        class="fa fa-check-circle text-color--green float-right"
                        aria-hidden="true"></i>
                </Tooltip>
            </template>
            <Tooltip
                v-else
                :settings="{ target: '.badgeTooltip' }">
                <i
                    class="fa fa-times-circle text-color--red float-right"
                    aria-hidden="true"></i>
            </Tooltip>
        </span>

        <button
            v-if="collapsible"
            type="button"
            :aria-label="trans('SHOW_MORE')"
            class="entity__collapsible"
            @click="toggleCollapse">
            <i
                class="fa"
                :class="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"
                aria-hidden="true"></i>
        </button>

        <input
            :id="entityId"
            ref="checkbox"
            v-model="isChecked"
            type="checkbox"
            class="entity__checkbox"
            :aria-labelledby="`entity__checkboxLabel--${ name }`"
            :name="name"
            :value="value"
            :disabled="isDisabled"
            @change="checkThis">

        <label
            :id="`entity__checkboxLabel--${ name }`"
            :data-area="name"
            class="entity__label"
            @click="singleCheck">
            <slot name="label"></slot>
            <span
                v-if="foundResults"
                :id="`entity__spanValue--${ name }`"
                class="font-italic">(<span
                    :data-value="foundResultsOriginal"
                    v-html="foundResults"></span>)
            </span>
        </label>

        <div :class="collapsed ? 'hidden' : 'padding-top-10 visible'">
            <slot name="target"></slot>
        </div>
    </div>
</template>

<script>
    import Translator from 'tools/trans';
    import { getFromTemplate } from 'tools/getFromHtml';
    import { searchFormSubmit } from './searchFormSubmit';
    import Tooltip from 'components/tooltip';

    // class="{{ (not activeCompany and entities.statistic.count|default("0") == "0") ? 'disabled text-color--gray' }} entity__label"
    // als Fallback fürs userFeature-Recht kann "SHOW_STATISTIC_PAID" genutzt werden, aber zurzeit ist eh in jedem <search-filter>-Element ein eigenes Recht angegeben.

    export default {
        components: {
            Tooltip,
        },
        model: {
            prop: 'checked',
            event: 'change',
        },
        props: {
            checked: { type: Boolean, default: false },
            disabled: { type: Boolean, default: false },
            entityId: { type: String, required: true },
            entityName: { type: String, default: null },
            value: { type: String, default: '1' },
            foundResults: { type: String, default: undefined },
            foundResultsOriginal: { type: String, default: '0' },
            // exclusive checkboxes can only be checked alone, no others can be checked simultaniously
            exclusive: { type: Boolean, default: false },
            autoSubmit: { type: Boolean, default: true },
            // should a label uncheck others (e.g. false on mobile, but true on desktop)
            labelSingleChoice: { type: Boolean, default: true },
            // does a user have access to this feature
            userFeature: { type: Boolean, default: true },
            showBadge: { type: Boolean, default: true },
            collapsible: { type: Boolean, default: false },
            isCollapsed: { type: Boolean, default: true },
        },
        data() {
            return {
                hasProRight: getFromTemplate('right', 'SHOW_PRO-BADGE'),
                hasCloudRight: getFromTemplate('right', 'ALLOW_CUSTOMER-CLOUD'),
                isChecked: this.checked,
                isDisabled: this.disabled,
                name: this.entityName ? this.entityName : this.entityId,
                collapsed: this.isCollapsed,
                children: [],
                childrenChecked: [],
            };
        },
        computed: {
            singleFilterChecked() {
                return this.$store.state.singleFilterChecked;
            },
        },
        watch: {
            singleFilterChecked(checkedFilter) {
                if (checkedFilter === this.entityId) {
                    this.isChecked = true;
                    this.setChildrenData('isChecked', true);
                    this.$emit('changeItself');
                } else if (checkedFilter !== 'MULTI_SELECT') {
                    this.isChecked = false;
                    this.$refs.checkbox.indeterminate = false;
                }
            },
            isChecked(value) {
                this.$emit('change');
                // if the singleFilterChecked is a child of current filter, update current checkbox status accordingly
                if (this.children.indexOf(this.singleFilterChecked) > -1) {
                    this.childrenCheckedSync();
                } else if (this.$refs.checkbox.indeterminate === false) {
                    this.setChildrenData('isChecked', value);
                }
            },
            childrenChecked(childrenChecked) {
                if (childrenChecked.length === 0) {
                    this.isChecked = false;
                    this.$refs.checkbox.indeterminate = false;
                } else if (this.children.length === childrenChecked.length) {
                    this.isChecked = true;
                    this.$refs.checkbox.indeterminate = false;
                } else {
                    this.$refs.checkbox.indeterminate = true;
                    this.isChecked = false;
                }
                this.$emit('changeItself');
            },
        },
        methods: {
            trans: Translator.trans,
            childrenCheckedSync() {
                this.childrenChecked = [];
                const childrenChecked = [];
                this.$slots.target.forEach(child => {
                    if (child.componentInstance && child.componentInstance.isChecked === true) {
                        childrenChecked.push(child.componentInstance.entityId);
                    }
                });
                this.childrenChecked = childrenChecked;
            },
            checkThis() {
                if (this.exclusive) {
                    // exclusive checkbox
                    // set exclusive filter and uncheck others
                    this.$store.commit('setExclusive', this.entityId);
                    this.$store.commit('setSingleFilter', this.entityId);
                } else if (this.$store.state.lastExclusiveFilter.length > 0) {
                    // normal checkbox
                    // if previous click was exclusive checkbox: check this and uncheck others
                    this.$store.commit('setSingleFilter', this.entityId);
                    this.$store.commit('unsetLastExclusive');
                } else {
                    // normal checkbox
                    // check this, do not uncheck others
                    this.$store.commit('setSingleFilter', 'MULTI_SELECT');
                }
                this.$emit('changeItself');
            },
            singleCheck() {
                if (this.labelSingleChoice) {
                    // normal label
                    // check this, uncheck others
                    this.$store.commit('setSingleFilter', this.entityId);
                    this.$refs.checkbox.indeterminate = false;
                    this.setChildrenData('isChecked', true);
                } else {
                    // multiselect label
                    // toggle this, but will still be checked for exclusive
                    this.isChecked = !this.isChecked;
                    this.checkThis();
                }
                this.fireChangeEvent();
                if (this.autoSubmit) {
                    searchFormSubmit();
                }
            },
            setChildrenData(key, value) {
                const _this = this; // eslint-disable-line @typescript-eslint/no-this-alias
                if (this.$slots.target) {
                    setTimeout(function() {
                        _this.$slots.target.forEach(child => {
                            if (child.componentInstance && key in child.componentInstance) {
                                child.componentInstance[key] = value;
                            }
                        });
                    }, 0);
                }
                this.fireChangeEvent();
            },
            fireChangeEvent() {
                this.$store.dispatch('filterChangeEvent');
            },
            toggleCollapse() {
                this.collapsed = !this.collapsed;
            },
            initChildrenUpdateListener() {
                this.$slots.target.forEach(child => {
                    if (child.componentInstance) {
                        child.componentInstance.$on('changeItself', () => {
                            this.$nextTick(() => {
                                this.childrenCheckedSync();
                            });
                        });
                    }
                });
            },
        },
        mounted() {
            if (this.$slots.target) {
                this.initChildrenUpdateListener();

                this.$slots.target.forEach(child => {
                    if (child.componentInstance) {
                        this.children.push(child.componentInstance.entityId);
                    }
                });
                this.childrenCheckedSync();
            }
        },
    };
</script>
